import {
  ETYKIETY_OCEN_SLOWNYCH,
  type OcenaSlowna,
  type WynikOceny,
} from "@shared/ocenaSlowna.js";
import {
  KODY_BLEDOW_TRENINGU,
  MAX_ZNAKOW_WIADOMOSCI_TRENINGU,
  komunikatBleduTreningu,
  type CwiczenieKatalogDto,
  type OcenaTuryDto,
  type PoziomTreningu,
  type PoziomTreninguDto,
  type TreningDto,
  type TreningListaDto,
} from "@shared/treningTypy.js";
import { ApiError } from "./api.js";
import { odmien } from "./polski.js";

/**
 * Trening ("Cwiczenia"): czysta logika ekranow, bez DOM-u i bez sieci (poza `sessionStorage` w szkicu odpowiedzi, z
 * jawnym fallbackiem). Komponenty tylko ukladaja to, co tu policzono: dzieki temu da sie to testowac bez renderowania.
 *
 * Opis calosci: docs/trening.md. Kontrakt z serwerem: shared/treningTypy.ts.
 */

// ---------------------------------------------------------------------------------------------------------------------
// Bledy
// ---------------------------------------------------------------------------------------------------------------------

/** Blad akcji treningu opisany dla uzytkownika: zdanie, ktore wolno pokazac, i informacja, czy ma sens ponowienie. */
export interface BladTreningu {
  /** Kod z serwera ("blad_modelu"), "siec" przy braku polaczenia albo "unknown". */
  kod: string;
  /** Status HTTP, albo null przy bledzie sieci. */
  status: number | null;
  /** Zdanie po polsku. Nigdy nazwa dostawcy ani modelu: serwer ich nie wysyla, a klient ich nie dokleja. */
  zdanie: string;
  /** True, gdy ten sam krok ma szanse sie udac za chwile (rozmowca jeszcze odpowiada, chwilowy blad modelu, siec). */
  mozeSprobowacPonownie: boolean;
}

const ZDANIE_SIECI = "Nie udało się połączyć z serwerem. Sprawdź połączenie i spróbuj jeszcze raz.";
const ZDANIE_SERWERA = "Serwer chwilowo nie odpowiada. Spróbuj jeszcze raz za chwilę.";
const ZDANIE_SESJI = "Twoja sesja wygasła. Odśwież stronę i zaloguj się ponownie.";

/**
 * Kod, ktory serwer zwraca (404) przy zakladaniu treningu, gdy analiza podana jako zrodlo (`zrodloAnalizyId`) nie istnieje
 * albo nie jest widoczna dla konta (docs/trening.md). Ogolne zdanie z shared/ ("Nie ma takiej analizy.") nic uzytkownikowi
 * nie podpowiada, a sam trening da sie wtedy zalozyc bez zrodla, wiec ekran mowi to wprost i przy ponowieniu pomija zrodlo.
 */
export const KOD_NIE_MA_ZRODLA = "nie_ma_takiej_sesji";

const ZDANIE_BRAKU_ZRODLA = "Nie znaleziono rozmowy, z której przychodzisz. Możesz zacząć to ćwiczenie bez niej.";

/** Kody, przy ktorych ponowienie TEGO SAMEGO kroku ma sens (reszta wymaga zmiany czegos po stronie uzytkownika). */
const KODY_DO_PONOWIENIA: ReadonlySet<string> = new Set(["trwa_odpowiedz", "blad_modelu", "zbyt_wiele_zadan"]);

/**
 * Kody, po ktorych ekran jest PRAWDOPODOBNIE nieaktualny (np. trening zakonczony w innej karcie): hook pobiera wtedy
 * trening jeszcze raz, zeby widok zgadzal sie z serwerem. To reakcja na akcje uzytkownika, nie odpytywanie w tle.
 */
const KODY_NIEAKTUALNEGO_EKRANU: ReadonlySet<string> = new Set(["trening_zakonczony", "limit_tur", "za_malo_tur"]);

export function czyEkranNieaktualny(kod: string): boolean {
  return KODY_NIEAKTUALNEGO_EKRANU.has(kod);
}

/**
 * Czy wynik WYSLANIA odpowiedzi (albo zakonczenia) jest NIEPEWNY: zerwane polaczenie, odpowiedz bez JSON-a z proxy (502,
 * 503, 504 od nginxa to kod "unknown"), odpowiedz o zlym ksztalcie ("zla_odpowiedz") albo 409 "trwa_odpowiedz" (poprzednie
 * zadanie nadal sie liczy). We wszystkich tych przypadkach serwer MOGL przyjac ture: zapisuje ja razem z odpowiedzia
 * rozmowcy dopiero po odpowiedzi modelu i robi to takze wtedy, gdy klient juz sie rozlaczyl. Ponowienie tego samego tekstu
 * bez sprawdzenia stanu dalo by DUPLIKAT odpowiedzi (druga tura z limitu, model dostaje powtorzony tekst). Bledy serwera z
 * kodem ("blad_modelu", "model_niedostepny", 4xx) mowia jasno, ze nic nie zapisano, wiec niepewne nie sa.
 */
export function czyWynikWysylkiNiepewny(blad: Pick<BladTreningu, "kod" | "status">): boolean {
  return (
    blad.kod === "siec" ||
    blad.kod === "trwa_odpowiedz" ||
    blad.kod === "zla_odpowiedz" ||
    (blad.kod === "unknown" && blad.status !== null && blad.status >= 500)
  );
}

/** Tekst odpowiedzi tak, jak zapisuje go serwer: konce wiersza ujednolicone, biale znaki na brzegach obciete. */
export function normalizujOdpowiedz(tekst: string): string {
  return tekst.replace(/\r\n?/g, "\n").trim();
}

/** Tresc ostatniej odpowiedzi uzytkownika w treningu albo null, gdy jeszcze zadnej nie ma. */
export function ostatniaOdpowiedzUcznia(trening: Pick<TreningDto, "wiadomosci">): string | null {
  for (let i = trening.wiadomosci.length - 1; i >= 0; i -= 1) {
    if (trening.wiadomosci[i].rola === "uczen") return trening.wiadomosci[i].tekst;
  }
  return null;
}

/**
 * Czy tura z tym tekstem WESZLA po stronie serwera: liczba odpowiedzi wzrosla wzgledem stanu sprzed wysylki, a ostatnia
 * odpowiedz to dokladnie ten tekst. Tylko wtedy ponowienie jest zbedne (a szkodliwe): ekran pokazuje stan z serwera.
 */
export function czyTuraWeszla(
  trening: Pick<TreningDto, "liczbaTurUcznia" | "wiadomosci">,
  tekst: string,
  liczbaTurPrzed: number,
): boolean {
  return trening.liczbaTurUcznia > liczbaTurPrzed && ostatniaOdpowiedzUcznia(trening) === normalizujOdpowiedz(tekst);
}

/** Opis bledu z samego kodu i statusu (`useZasob` oddaje tylko je). Kod "siec" znaczy brak odpowiedzi serwera. */
export function opisBleduZKodu(kod: string, status: number | null): BladTreningu {
  // Przed zdaniem ogolnym z shared/ ("Nie ma takiej analizy."): tu wiemy, co z tym zrobic (zaczac bez zrodla).
  if (kod === KOD_NIE_MA_ZRODLA) {
    return { kod, status, zdanie: ZDANIE_BRAKU_ZRODLA, mozeSprobowacPonownie: true };
  }
  if (kod === "siec" || (status === null && !(KODY_BLEDOW_TRENINGU as readonly string[]).includes(kod))) {
    return { kod: "siec", status: null, zdanie: ZDANIE_SIECI, mozeSprobowacPonownie: true };
  }
  if ((KODY_BLEDOW_TRENINGU as readonly string[]).includes(kod)) {
    return { kod, status, zdanie: komunikatBleduTreningu(kod), mozeSprobowacPonownie: KODY_DO_PONOWIENIA.has(kod) };
  }
  if (status === 401) {
    return { kod, status, zdanie: ZDANIE_SESJI, mozeSprobowacPonownie: false };
  }
  if (status !== null && status >= 500) {
    return { kod, status, zdanie: ZDANIE_SERWERA, mozeSprobowacPonownie: true };
  }
  return { kod, status, zdanie: komunikatBleduTreningu(kod), mozeSprobowacPonownie: true };
}

/** Blad z `catch`: ApiError niesie kod i status, wszystko inne (fetch bez odpowiedzi) to brak polaczenia. */
export function opisBleduTreningu(blad: unknown): BladTreningu {
  if (blad instanceof ApiError) return opisBleduZKodu(blad.code, blad.status);
  return opisBleduZKodu("siec", null);
}

// ---------------------------------------------------------------------------------------------------------------------
// Plakietki ocen (ST-3: kolor nigdy sam, zawsze slowo)
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Warianty plakietek treningu: podzbior nazw z components/ui/Badge.tsx (tu bez importu komponentu: lib nie zalezy od
 * widoku). Ocena uzywa wylacznie wariantow oceny: zloto (najlepsze), szarosc (do wymiany) i neutralny (srodek bez
 * oznaczenia albo brak oceny), wg decyzji autora metodyki z 29.10.2026. Zielen, czerwien i bursztyn statusow nie
 * wchodza tu wcale: w widokach oceny bursztyn znaczy "najlepsze". Jedyny prawdziwy status to "Trwa" (info).
 */
export type WariantPlakietki = "zloto" | "szarosc" | "info" | "neutralny";

export interface Plakietka {
  wariant: WariantPlakietki;
  etykieta: string;
}

/**
 * Te same grupy ocen co w wyniku rozmowy (lib/wynikUzytkownika.ts, tonOceny): "Bardzo dobrze" i "Dobrze" (dotad zielone)
 * to zloto, "Średnio" (dotad bursztynowe) jest bez oznaczenia, "Słabo" (dotad czerwone) to szarosc.
 */
const WARIANT_OCENY: Readonly<Record<OcenaSlowna, WariantPlakietki>> = {
  "bardzo-dobrze": "zloto",
  dobrze: "zloto",
  srednio: "neutralny",
  slabo: "szarosc",
};

export const PLAKIETKA_ZA_MALO_DANYCH: Plakietka = { wariant: "neutralny", etykieta: "Za mało danych" };

/** Ocena slowna jako plakietka. Slowo spoza skali (starszy albo nowszy serwer) nie wywraca ekranu: neutralna plakietka. */
export function plakietkaOceny(ocena: OcenaSlowna): Plakietka {
  const etykieta = ETYKIETY_OCEN_SLOWNYCH[ocena];
  if (!etykieta) return PLAKIETKA_ZA_MALO_DANYCH;
  return { wariant: WARIANT_OCENY[ocena], etykieta };
}

/** Wynik oceny treningu: ocena slowna albo (przy za malej liczbie odpowiedzi i przy braku oceny) "Za mało danych". */
export function plakietkaWyniku(wynik: WynikOceny | null | undefined): Plakietka {
  if (!wynik || wynik.rodzaj !== "ocena") return PLAKIETKA_ZA_MALO_DANYCH;
  return plakietkaOceny(wynik.ocena);
}

/** Wiersz listy treningow: "Trwa" dla otwartego, ocena slowna albo "Za mało danych" dla zakonczonego. */
export function plakietkaListy(trening: Pick<TreningListaDto, "status" | "ocena">): Plakietka {
  if (trening.status === "ACTIVE") return { wariant: "info", etykieta: "Trwa" };
  return trening.ocena ? plakietkaOceny(trening.ocena) : PLAKIETKA_ZA_MALO_DANYCH;
}

/** Ocena jednej odpowiedzi: "Zrobione" (zloto) / "Do poprawy" (szarosc) / "Nie dotyczy" (bez oznaczenia). */
export function plakietkaTury(tura: Pick<OcenaTuryDto, "stosuje" | "spelnia">): Plakietka {
  if (!tura.stosuje) return { wariant: "neutralny", etykieta: "Nie dotyczy" };
  return tura.spelnia ? { wariant: "zloto", etykieta: "Zrobione" } : { wariant: "szarosc", etykieta: "Do poprawy" };
}

export type WynikTury = "zrobione" | "do-poprawy" | "nie-dotyczy";

export function wynikTury(tura: Pick<OcenaTuryDto, "stosuje" | "spelnia">): WynikTury {
  if (!tura.stosuje) return "nie-dotyczy";
  return tura.spelnia ? "zrobione" : "do-poprawy";
}

/**
 * Powod braku oceny slownej w treningu (stoi obok plakietki "Za mało danych", wiec nie powtarza tych slow). Wlasne zdanie
 * zamiast `opisZaMaloDanych` z shared/: tamto mowi o "uwagach" modelu w analizie rozmowy, a tu liczy sie, w ilu
 * odpowiedziach cwiczenie MIALO ZASTOSOWANIE (mocne + do poprawy).
 */
export function opisZaMaloDanychTreningu(wynik: Extract<WynikOceny, { rodzaj: "za-malo-danych" }>): string {
  const razem = wynik.mocne + wynik.doPoprawy;
  if (razem === 0) {
    return "W żadnej z Twoich odpowiedzi to ćwiczenie nie miało zastosowania, więc nie ma z czego wystawić oceny.";
  }
  const odpowiedzi = odmien(razem, "odpowiedzi", "odpowiedziach", "odpowiedziach");
  return `Ćwiczenie miało zastosowanie w ${razem} ${odpowiedzi}, a do oceny potrzeba co najmniej ${wynik.potrzeba}.`;
}

/** Fakty stojace za ocena slowna ("Ćwiczenie miało zastosowanie w 4 odpowiedziach. Zrobione: 3, do poprawy: 1."). */
export function opisLiczbOceny(wynik: WynikOceny): string {
  const razem = wynik.mocne + wynik.doPoprawy;
  if (razem === 0) return "";
  const odpowiedzi = odmien(razem, "odpowiedzi", "odpowiedziach", "odpowiedziach");
  return `Ćwiczenie miało zastosowanie w ${razem} ${odpowiedzi}. Zrobione: ${wynik.mocne}, do poprawy: ${wynik.doPoprawy}.`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Stan rozmowy
// ---------------------------------------------------------------------------------------------------------------------

type StanLiczb = Pick<TreningDto, "liczbaTurUcznia" | "maxTurUcznia" | "minTurDoOceny">;

/** "3 z 20": ile odpowiedzi uzytkownik juz wyslal i ile miesci trening. */
export function licznikTur(trening: Pick<TreningDto, "liczbaTurUcznia" | "maxTurUcznia">): string {
  return `${trening.liczbaTurUcznia} z ${trening.maxTurUcznia}`;
}

/** Osiagnieto sufit odpowiedzi: pole jest zablokowane, zostaje tylko zakonczenie. */
export function czyLimitTur(trening: Pick<TreningDto, "liczbaTurUcznia" | "maxTurUcznia">): boolean {
  return trening.liczbaTurUcznia >= trening.maxTurUcznia;
}

/** Ile odpowiedzi brakuje do tego, zeby dalo sie zakonczyc z ocena (0, gdy juz wystarczy). */
export function ileOdpowiedziDoOceny(trening: Pick<TreningDto, "liczbaTurUcznia" | "minTurDoOceny">): number {
  return Math.max(0, trening.minTurDoOceny - trening.liczbaTurUcznia);
}

export function czyMoznaZakonczyc(trening: Pick<TreningDto, "status"> & StanLiczb): boolean {
  return trening.status === "ACTIVE" && ileOdpowiedziDoOceny(trening) === 0;
}

/** Zdanie pod nieaktywnym przyciskiem "Zakończ" (S-10: powod nieoczywisty, wiec zapisany tekstem, nie samym wygaszeniem). */
export function powodBlokadyZakonczenia(trening: Pick<TreningDto, "status"> & StanLiczb): string | null {
  if (trening.status !== "ACTIVE") return null;
  const brak = ileOdpowiedziDoOceny(trening);
  if (brak === 0) return null;
  return `Napisz jeszcze ${brak} ${odmien(brak, "odpowiedź", "odpowiedzi", "odpowiedzi")}, żeby zobaczyć ocenę.`;
}

/**
 * Ostatnia wiadomosc w otwartym treningu jest od uzytkownika: rozmowca jeszcze nie odpowiedzial. Pole zostaje wtedy
 * zablokowane, a uzytkownik moze sprawdzic rozmowe jeszcze raz.
 *
 * UWAGA: obecny serwer NIGDY nie tworzy takiego stanu (zapisuje odpowiedz uzytkownika razem z odpowiedzia rozmowcy, po
 * odpowiedzi modelu), wiec ta galaz jest tylko obrona na wypadek innego zrodla danych. Przed duplikatem odpowiedzi po
 * odswiezeniu strony w trakcie tury chroni znacznik niepewnej wysylki (`odczytajNiepewnaWysylke`), a nie ten stan.
 */
export function czyOczekujeNaRozmowce(trening: Pick<TreningDto, "status" | "wiadomosci">): boolean {
  if (trening.status !== "ACTIVE") return false;
  const ostatnia = trening.wiadomosci[trening.wiadomosci.length - 1];
  return ostatnia !== undefined && ostatnia.rola === "uczen";
}

// ---------------------------------------------------------------------------------------------------------------------
// Teksty i daty
// ---------------------------------------------------------------------------------------------------------------------

/** Tekst skrocony do `max` znakow na granicy wyrazu, z trzema kropkami (bez znaku wielokropka: zakaz firmowy). */
export function skrocTekst(tekst: string, max = 90): string {
  const czysty = tekst.replace(/\s+/g, " ").trim();
  if (czysty.length <= max) return czysty;
  const ciecie = czysty.lastIndexOf(" ", max);
  const koniec = ciecie > max * 0.6 ? ciecie : max;
  return `${czysty.slice(0, koniec).trimEnd()}...`;
}

/** "13 paź 2026, 14:05". Zepsuta data z serwera daje pusty tekst, nie "Invalid Date". */
export function formatDatyTreningu(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "";
  return new Intl.DateTimeFormat("pl-PL", { dateStyle: "medium", timeStyle: "short" }).format(data);
}

// ---------------------------------------------------------------------------------------------------------------------
// Katalog cwiczen i parametry zapytania
// ---------------------------------------------------------------------------------------------------------------------

export interface GrupaCwiczen {
  lensSlug: string;
  /** Tytul parametru po polsku. */
  parametr: string;
  cwiczenia: CwiczenieKatalogDto[];
}

/** Klucz cwiczenia do porownan i do `value` pola wyboru. Nigdy go nie parsujemy: szukamy cwiczenia po rownosci klucza. */
export function kluczCwiczenia(cwiczenie: Pick<CwiczenieKatalogDto, "lensSlug" | "indeks">): string {
  return `${cwiczenie.lensSlug}:${cwiczenie.indeks}`;
}

/** Cwiczenia pogrupowane wg parametru, w kolejnosci pierwszego wystapienia w katalogu. */
export function grupujCwiczenia(cwiczenia: readonly CwiczenieKatalogDto[]): GrupaCwiczen[] {
  const grupy = new Map<string, GrupaCwiczen>();
  for (const cwiczenie of cwiczenia) {
    const grupa = grupy.get(cwiczenie.lensSlug);
    if (grupa) grupa.cwiczenia.push(cwiczenie);
    else grupy.set(cwiczenie.lensSlug, { lensSlug: cwiczenie.lensSlug, parametr: cwiczenie.parametr, cwiczenia: [cwiczenie] });
  }
  return [...grupy.values()];
}

/** Parametry zapytania, z ktorymi uzytkownik przychodzi z ekranu wyniku: `/cwiczenia?parametr=...&cwiczenie=...&zrodlo=...`. */
export interface ParametryCwiczenia {
  /** Slug parametru oceny (lensSlug), albo null. */
  parametr: string | null;
  /** Numer cwiczenia w parametrze, od 0, albo null. */
  cwiczenie: number | null;
  /** Id analizy, z ktorej uzytkownik przyszedl, albo null. */
  zrodlo: string | null;
}

/** Slug i id to krotkie znaki bez spacji. Cokolwiek innego w adresie jest ignorowane, nie przepuszczane do serwera. */
const WZORZEC_IDENTYFIKATORA = /^[A-Za-z0-9_-]{1,64}$/;
const WZORZEC_INDEKSU = /^\d{1,3}$/;

export function odczytajParametryCwiczenia(zapytanie: URLSearchParams | string): ParametryCwiczenia {
  const params = typeof zapytanie === "string" ? new URLSearchParams(zapytanie) : zapytanie;
  const parametr = params.get("parametr");
  const cwiczenie = params.get("cwiczenie");
  const zrodlo = params.get("zrodlo");
  return {
    parametr: parametr !== null && WZORZEC_IDENTYFIKATORA.test(parametr) ? parametr : null,
    cwiczenie: cwiczenie !== null && WZORZEC_INDEKSU.test(cwiczenie) ? Number(cwiczenie) : null,
    zrodlo: zrodlo !== null && WZORZEC_IDENTYFIKATORA.test(zrodlo) ? zrodlo : null,
  };
}

/** Adres ekranu cwiczen z wybranym cwiczeniem: ten sam ksztalt czyta `odczytajParametryCwiczenia`. */
export function adresCwiczen(wybor: { lensSlug?: string; cwiczenieIndeks?: number; zrodloAnalizyId?: string } = {}): string {
  const params = new URLSearchParams();
  if (wybor.lensSlug) params.set("parametr", wybor.lensSlug);
  if (wybor.cwiczenieIndeks !== undefined) params.set("cwiczenie", String(wybor.cwiczenieIndeks));
  if (wybor.zrodloAnalizyId) params.set("zrodlo", wybor.zrodloAnalizyId);
  const tekst = params.toString();
  return tekst ? `/cwiczenia?${tekst}` : "/cwiczenia";
}

/**
 * Cwiczenie, ktore ma byc wybrane na starcie: to z adresu (parametr + cwiczenie), a gdy podano sam parametr, pierwsze z
 * tego parametru. Cokolwiek, czego w katalogu nie ma, odpada do pierwszego cwiczenia katalogu. Pusty katalog daje null.
 */
export function wybierzCwiczenieStartowe(
  cwiczenia: readonly CwiczenieKatalogDto[],
  parametry: Pick<ParametryCwiczenia, "parametr" | "cwiczenie">,
): CwiczenieKatalogDto | null {
  if (parametry.parametr !== null) {
    const zParametru = cwiczenia.filter((c) => c.lensSlug === parametry.parametr);
    if (parametry.cwiczenie !== null) {
      const dokladne = zParametru.find((c) => c.indeks === parametry.cwiczenie);
      if (dokladne) return dokladne;
    }
    if (zParametru[0]) return zParametru[0];
  }
  return cwiczenia[0] ?? null;
}

/** Opis poziomu do banera pod przelacznikiem. */
export function opisPoziomu(poziomy: readonly PoziomTreninguDto[], poziom: PoziomTreningu): string {
  return poziomy.find((p) => p.poziom === poziom)?.opis ?? "";
}

// ---------------------------------------------------------------------------------------------------------------------
// Szkic odpowiedzi w sessionStorage
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Szkic odpowiedzi trzymamy w `sessionStorage` PER TRENING: przejscie na inna strone i powrot, odswiezenie karty albo
 * porazka wyslania nie kasuja tego, co uzytkownik pisal. Storage bywa niedostepny (tryb prywatny, zablokowane dane, jsdom
 * bez okna), wiec KAZDY odczyt i zapis jest w try/catch, a ekran dziala tak samo bez niego (tylko bez pamieci szkicu).
 */
const PREFIKS_SZKICU = "convolens:trening:szkic:";

function domyslnyMagazyn(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function odczytajSzkic(idTreningu: string, magazyn: Storage | null = domyslnyMagazyn()): string {
  try {
    const tekst = magazyn?.getItem(`${PREFIKS_SZKICU}${idTreningu}`) ?? "";
    return tekst.slice(0, MAX_ZNAKOW_WIADOMOSCI_TRENINGU);
  } catch {
    return "";
  }
}

/** Pusty tekst usuwa wpis: nie trzymamy pustych kluczy po kazdej wyslanej odpowiedzi. */
export function zapiszSzkic(idTreningu: string, tekst: string, magazyn: Storage | null = domyslnyMagazyn()): void {
  try {
    if (!magazyn) return;
    const klucz = `${PREFIKS_SZKICU}${idTreningu}`;
    if (tekst === "") magazyn.removeItem(klucz);
    else magazyn.setItem(klucz, tekst);
  } catch {
    // Pelny albo zablokowany storage: szkic po prostu nie przetrwa odswiezenia.
  }
}

export function usunSzkic(idTreningu: string, magazyn: Storage | null = domyslnyMagazyn()): void {
  zapiszSzkic(idTreningu, "", magazyn);
}

// ---------------------------------------------------------------------------------------------------------------------
// Wysylka o niepewnym wyniku (sessionStorage, per trening)
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Znacznik "ta odpowiedz mogla juz dojsc do serwera": tekst i liczba odpowiedzi sprzed wysylki. Stoi od chwili wyslania do
 * jej pewnego wyniku, takze przez odswiezenie strony w trakcie tury (serwer dokancza ture po zerwaniu polaczenia, a po
 * odswiezeniu szkic wraca do pola i bez znacznika dalo by sie wyslac ten sam tekst drugi raz). Przed ponowieniem TEGO SAMEGO
 * tekstu hook pobiera trening i sprawdza `czyTuraWeszla`. Storage bywa niedostepny: wtedy zostaje tylko sprawdzenie zaraz
 * po bledzie, w obrebie tej samej karty.
 */
const PREFIKS_WYSYLKI = "convolens:trening:wysylka:";

export interface NiepewnaWysylka {
  /** Tekst po normalizacji (`normalizujOdpowiedz`). */
  tekst: string;
  /** Liczba odpowiedzi uzytkownika w treningu przed wysylka. */
  przed: number;
}

export function odczytajNiepewnaWysylke(idTreningu: string, magazyn: Storage | null = domyslnyMagazyn()): NiepewnaWysylka | null {
  try {
    const surowe = magazyn?.getItem(`${PREFIKS_WYSYLKI}${idTreningu}`);
    if (!surowe) return null;
    const dane = JSON.parse(surowe) as Partial<NiepewnaWysylka> | null;
    if (dane === null || typeof dane.tekst !== "string" || typeof dane.przed !== "number" || !Number.isFinite(dane.przed)) return null;
    return { tekst: dane.tekst, przed: dane.przed };
  } catch {
    return null;
  }
}

export function zapiszNiepewnaWysylke(idTreningu: string, dane: NiepewnaWysylka, magazyn: Storage | null = domyslnyMagazyn()): void {
  try {
    magazyn?.setItem(`${PREFIKS_WYSYLKI}${idTreningu}`, JSON.stringify(dane));
  } catch {
    // Pelny albo zablokowany storage: znacznik po prostu nie przetrwa odswiezenia.
  }
}

export function usunNiepewnaWysylke(idTreningu: string, magazyn: Storage | null = domyslnyMagazyn()): void {
  try {
    magazyn?.removeItem(`${PREFIKS_WYSYLKI}${idTreningu}`);
  } catch {
    // Jak wyzej.
  }
}
